import ko from "knockout";
import _ from "lodash";
import LOG from "services/loggingService";
import featureToggleService, {
    FeatureToggle,
} from "services/featureToggleService";

function getParticipantPurpose(participant) {
    return (participant.purpose || "unknown").toLowerCase();
}

const guidPattern =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
function isGuid(maybeGuid) {
    return guidPattern.test(maybeGuid);
}

/**
 * Generates a unique group ID for timeline visualization based on participant and session properties.
 * This ID determines which timeline row a session's segments will appear in.
 *
 * When REC_9720_TIMELINE_IMPROVEMENTS feature toggle is enabled:
 * - Removed 'purpose' from most group IDs to prevent duplicate rows during purpose transitions
 * - Only user/agent participants include purpose to handle Supervisor/Agent scenario
 * - Uses flowId when available to group flow-based participants correctly
 *
 * When feature toggle is disabled: Falls back to original grouping logic
 */
function getParticipantGroup(participant, session) {
    if (
        featureToggleService.isEnabled(
            FeatureToggle.REC_9720_TIMELINE_IMPROVEMENTS
        )
    ) {
        return getParticipantGroupNew(participant, session);
    } else {
        return getParticipantGroupLegacy(
            participant,
            session,
            session.mediaType
        );
    }
}

function getParticipantGroupNew(participant, session) {
    const { userId, participantId } = participant;
    const purpose = getParticipantPurpose(participant);
    const { mediaType, direction } = session;
    const queueId = session.segments?.[0]?.queueId;
    const flowId = session.flow?.flowId;

    // User/agent participants are grouped by direction to show separate timeline rows for inbound vs outbound sessions
    if (userId) {
        return `${userId}_${direction}_${purpose}_${mediaType}`;
    }

    if (purpose === "acd" && queueId) {
        return `${queueId}_${mediaType}`;
    }

    if (flowId) {
        return `${flowId}_${mediaType}`;
    }
    return `${participantId}_${mediaType}`;
}

/**
 * ORIGINAL: Legacy grouping logic that includes purpose in most group IDs
 */
function getParticipantGroupLegacy(
    participant,
    interaction,
    communicationType
) {
    const id = participant.participantId;
    const userId = participant.userId;
    const type = getParticipantPurpose(participant);
    const direction = interaction.direction;

    if (participant.userId) {
        // This is a user, so only Inbound or Outbound
        return userId + "_" + direction + "_" + type + "_" + communicationType;
    } else if (
        participant.purpose === "acd" &&
        interaction.segments &&
        interaction.segments[0].queueId
    ) {
        return (
            interaction.segments[0].queueId +
            "_" +
            type +
            "_" +
            communicationType
        );
    } else if (interaction.flow?.flowId) {
        return interaction.flow.flowId + "_" + type + "_" + communicationType;
    } else {
        return id + "_" + type + "_" + communicationType;
    }
}

/**
 * Preprocesses session data to handle dynamic timeline updates and prevent duplicate rows.
 * This function manages session state tracking and migrates timeline items when participant
 * properties change during live conversations.
 *
 * Key responsibilities:
 * 1. Track session state changes (purpose transitions, flow additions)
 * 2. Migrate existing timeline items to new groups when groupId changes
 * 3. Maintain session state map for efficient lookups during polling updates
 *
 * @param {Object} participant - The participant object containing purpose, userId, etc.
 * @param {Object} session - The session object with sessionId, flow, mediaType, direction
 * @param {DataSet} existingTimelineData - vis-timeline DataSet containing timeline items
 * @param {Map} sessionStateMap - Map tracking session states by sessionId
 */
function preprocessSession(
    participant,
    session,
    existingTimelineData,
    sessionStateMap
) {
    // Validate session has required sessionId for state tracking
    if (!session?.sessionId) {
        LOG.warn("Missing sessionId for session", session);
        return;
    }

    const sessionKey = session.sessionId;
    const existingState = sessionStateMap.get(sessionKey);

    if (existingState) {
        // Detect state changes that require timeline row migration
        const purposeChanged = existingState.purpose !== participant.purpose;
        const flowAdded = session.flow?.flowId && !existingState.flowId;

        // Only process if meaningful changes occurred that affect grouping
        if (purposeChanged || flowAdded) {
            const newGroupId = getParticipantGroupNew(participant, session);

            if (existingState.groupId !== newGroupId) {
                try {
                    // Migrate all timeline items from old group to new group
                    // This prevents duplicate rows when participant properties change
                    const itemsToUpdate = existingTimelineData.get({
                        filter: (item) => item.group === existingState.groupId,
                    });

                    const updatedItems = itemsToUpdate.map((item) => ({
                        ...item,
                        group: newGroupId,
                    }));

                    existingTimelineData.update(updatedItems);
                } catch (error) {
                    LOG.error("Failed to update timelineData items:", error);
                    return;
                }
            }

            // Update session state tracking with new values
            sessionStateMap.set(sessionKey, {
                groupId: newGroupId,
                purpose: participant.purpose,
                flowId: session.flow?.flowId,
            });
        }
    } else {
        // Initialize state tracking for new sessions
        const groupId = getParticipantGroupNew(participant, session);
        sessionStateMap.set(sessionKey, {
            groupId,
            purpose: participant.purpose,
            flowId: session.flow?.flowId,
        });
    }
}

function getParticipantName(participant, baseConversationParticipant) {
    if (
        !participant.participantName &&
        (participant.purpose === "customer" ||
            participant.purpose === "external")
    ) {
        return ko.i18n.renderString(
            "conversationTimeline.participantInfo.guest"
        );
    } else if (
        !participant.participantName &&
        (participant.purpose === "agent" || participant.purpose === "user")
    ) {
        return ko.i18n.renderString(
            "conversationTimeline.participantInfo.noName"
        );
    } else if (
        participant.purpose === "voicemail" &&
        isGuid(participant.participantName)
    ) {
        // Voicemail participants have a guid name. Hide it.
        return "";
    } else if (
        participant.purpose === "acd" &&
        !participant.participantName &&
        baseConversationParticipant &&
        baseConversationParticipant.queueName
    ) {
        return baseConversationParticipant.queueName;
    } else if (
        participant.purpose === "botflow" ||
        participant.purpose === "voicesurveyflow"
    ) {
        return participant.sessions[0]
            ? participant.sessions[0].flow?.flowName || ""
            : "";
    }
    return participant.participantName;
}

function isConversationActive(conversation) {
    const sessions = _.flatMap(conversation.participants, "sessions");
    return _.some(sessions, { state: "connected" });
}

function roundMillisecondsToNearestSecond(value) {
    return Math.round(value / 1000) * 1000;
}

/**
 * Creates a new Map for tracking session state during timeline processing.
 *
 * Map Structure:
 * - Key: sessionId (string) - Unique session identifier from conversation data
 * - Value: {
 *     groupId: (string) - Timeline group ID determining which row segments appear in
 *     purpose: (string) - Participant purpose (agent, customer, acd, botflow, etc.)
 *     flowId: (string|undefined) - Flow ID for bot/IVR participants, undefined for others
 *   }
 *
 * This map enables detection of session changes that require timeline item migration,
 * such as purpose transitions (external → customer) or flow additions for bots.
 *
 * @returns {Map} Empty Map for session state tracking
 */
function createSessionStateMap() {
    return new Map();
}

export default {
    getParticipantGroup: getParticipantGroup,
    getParticipantPurpose: getParticipantPurpose,
    getParticipantName: getParticipantName,
    isConversationActive: isConversationActive,
    roundMillisecondsToNearestSecond: roundMillisecondsToNearestSecond,
    preprocessSession: preprocessSession,
    createSessionStateMap: createSessionStateMap,
};
